import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
const T = { bg:'#f0f5e8', cream:'#fffef0', greenDk:'#1b5e20', maroon:'#6d1b1b', orange:'#e65100', border:'#c8e6c9', header:'linear-gradient(135deg,#090b72,#1a237e)' };

// 16 base thithis — user picks one, we match both paksham variants via number
// ThithiNumber mapping: 1 or 16 = ப்ரதமை, 2 or 17 = த்விதீயை, ... 14 or 29 = சதுர்தசி
// 15 = பௌர்ணமி, 0 or 30 = அமாவாசை
const BASE_THITHIS = [
  { no: 1, name: 'ப்ரதமை', nums: [1, 16] },
  { no: 2, name: 'த்விதீயை', nums: [2, 17] },
  { no: 3, name: 'த்ருதீயை', nums: [3, 18] },
  { no: 4, name: 'சதுர்த்தி', nums: [4, 19] },
  { no: 5, name: 'பஞ்சமி', nums: [5, 20] },
  { no: 6, name: 'ஷஷ்டி', nums: [6, 21] },
  { no: 7, name: 'ஸப்தமி', nums: [7, 22] },
  { no: 8, name: 'அஷ்டமி', nums: [8, 23] },
  { no: 9, name: 'நவமி', nums: [9, 24] },
  { no: 10, name: 'தசமி', nums: [10, 25] },
  { no: 11, name: 'ஏகாதசி', nums: [11, 26] },
  { no: 12, name: 'த்வாதசி', nums: [12, 27] },
  { no: 13, name: 'த்ரயோதசி', nums: [13, 28] },
  { no: 14, name: 'சதுர்தசி', nums: [14, 29] },
  { no: 15, name: 'பௌர்ணமி', nums: [15] },
  { no: 16, name: 'அமாவாசை', nums: [0, 30] },
];

const T_MONTHS_EN = ['Chithirai','Vaikasi','Aani','Aadi','Aavani','Purattasi','Iyypasi','Karthigai','Margazhi','Thai','Masi','Panguni'];
const T_MONTHS_TA = ['சித்திரை','வைகாசி','ஆனி','ஆடி','ஆவணி','புரட்டாசி','ஐப்பசி','கார்த்திகை','மார்கழி','தை','மாசி','பங்குனி'];
const DAYS_TA = ['ஞாயிறு','திங்கள்','செவ்வாய்','புதன்','வியாழன்','வெள்ளி','சனி'];

const ShraaddhaThithiPage = () => {
  const [allData, setAllData] = useState([]);
  const [selThithi, setSelThithi] = useState('');
  const [selPaksham, setSelPaksham] = useState('all');
  const [selMonth, setSelMonth] = useState('all');
  const [selYear, setSelYear] = useState('current');
  const [loading, setLoading] = useState(true);

  const currentYear = new Date().getFullYear();
  // 3-year window: previous, current, next
  const threeYearWindow = [currentYear - 1, currentYear, currentYear + 1];

  useEffect(() => {
    setLoading(true);
    axios.get('/data/panchangam_data-2025-to-2028.json').then(r => {
      // Only keep rows that have ShraadhdhaThithiNameInTamil
      const shraadhdhaRows = r.data.filter(d => d.ShraadhdhaThithiNameInTamil && d.ShraadhdhaThithiNameInTamil.trim() !== '');
      setAllData(shraadhdhaRows);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Get available years from data
  const availableYears = useMemo(() => {
    const yrs = new Set();
    allData.forEach(d => {
      if (d.EnglishDate) {
        const p = d.EnglishDate.split('/');
        yrs.add(parseInt(p[2]));
      }
    });
    return Array.from(yrs).sort();
  }, [allData]);

  // Determine if selected year is within the 3-year window
  const selectedYearNum = selYear === 'current' ? currentYear : parseInt(selYear);
  const isInWindow = threeYearWindow.includes(selectedYearNum);

  // Filter data based on selections
  const filtered = useMemo(() => {
    return allData.filter(d => {
      if (!d.EnglishDate) return false;
      const p = d.EnglishDate.split('/');
      const rowYear = parseInt(p[2]);

      // Year filter
      if (rowYear !== selectedYearNum) return false;

      // Thithi filter — use ShraadhdhaThithiNumber with either-or mapping
      // e.g. user picks ப்ரதமை → match ThithiNumber 1 OR 16
      if (selThithi) {
        const selEntry = BASE_THITHIS.find(t => t.name === selThithi);
        if (selEntry) {
          const tNo = parseInt(d.ThithiNumber || d.ShraadhdhaThithiNumber || 0);
          if (!selEntry.nums.includes(tNo)) return false;
        }
      }

      // Paksham filter — use PakshamNameInTamil text (safe for all year formats:
      // older years use ThithiNumber 1-15, newer years use 1-30)
      if (selPaksham !== 'all') {
        if (selPaksham === 'sukla' && !d.PakshamNameInTamil?.includes('சுக்ல')) return false;
        if (selPaksham === 'krishna' && !d.PakshamNameInTamil?.includes('கிருஷ்ண')) return false;
      }

      // Tamil month filter — MANDATORY for years outside the 3-year window
      if (!isInWindow && selMonth === 'all') return false;
      if (selMonth !== 'all') {
        // Match Tamil month name (handle both Tamil and English month names in data)
        const monthIdx = T_MONTHS_TA.indexOf(selMonth);
        if (monthIdx >= 0) {
          const matchTa = d.TamilMonth?.includes(T_MONTHS_TA[monthIdx]);
          const matchEn = d.TamilMonth?.includes(T_MONTHS_EN[monthIdx]);
          if (!matchTa && !matchEn) return false;
        }
      }

      return true;
    });
  }, [allData, selectedYearNum, selThithi, selPaksham, selMonth, isInWindow]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const p = dateStr.split('/');
    const d = new Date(parseInt(p[2]), parseInt(p[0])-1, parseInt(p[1]));
    return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()} ${DAYS_TA[d.getDay()]}`;
  };

  return (
    <div style={{background:T.bg,minHeight:'80vh',padding:'10px'}}>
      {/* Header */}
      <div style={{background:T.header,padding:'12px',borderRadius:'10px',textAlign:'center',marginBottom:'10px'}}>
        <img src="/images/rituals/tharpana-plate-direction.jpg" alt="தர்ப்பணம்" style={{height:'50px',borderRadius:'8px',marginBottom:'6px',border:'2px solid #f0d48a'}} />
        <h1 style={{color:'#fff',margin:0,fontSize:'1rem',fontWeight:900}}>🙏 ச்ராத்த திதி தேடல்</h1>
        <div style={{color:'#c8e6c9',fontSize:'0.7rem'}}>Shradhdha Thithi Finder — Panchangam Based</div>
      </div>

      {/* Ritual reference images */}
      <div style={{display:'flex',gap:'6px',marginBottom:'10px',overflowX:'auto',padding:'2px'}}>
        <img src="/images/rituals/pithru-vargam.JPG" alt="பித்ரு வர்கம்" style={{height:'55px',borderRadius:'6px',border:'1px solid '+T.border}} />
        <img src="/images/rituals/maathru-vargam.JPG" alt="மாத்ரு வர்கம்" style={{height:'55px',borderRadius:'6px',border:'1px solid '+T.border}} />
        <img src="/images/rituals/koorcham.JPG" alt="கூர்ச்சம்" style={{height:'55px',borderRadius:'6px',border:'1px solid '+T.border}} />
        <img src="/images/rituals/pithru-names.JPG" alt="பித்ரு பெயர்கள்" style={{height:'55px',borderRadius:'6px',border:'1px solid '+T.border}} />
      </div>

      {/* ─── Filter Form ─── */}
      <div style={{background:T.cream,padding:'10px',borderRadius:'10px',border:'2px solid '+T.greenDk,marginBottom:'10px'}}>
        <div style={{fontWeight:800,fontSize:'0.85rem',color:T.greenDk,marginBottom:'8px',textAlign:'center'}}>🔍 ச்ராத்த திதி தேடுக</div>

        {/* Year */}
        <div style={{marginBottom:'6px'}}>
          <label style={{fontSize:'0.7rem',fontWeight:700,color:T.greenDk}}>📅 ஆண்டு</label>
          <select value={selYear} onChange={e=>{setSelYear(e.target.value); if(!threeYearWindow.includes(parseInt(e.target.value))) setSelMonth(T_MONTHS_TA[0]);}}
            style={{width:'100%',padding:'8px',borderRadius:'6px',border:'1px solid #ccc',fontWeight:700,fontSize:'0.8rem'}}>
            {availableYears.map(y => (
              <option key={y} value={y}>
                {y} {y===currentYear ? '(நடப்பு)' : y===currentYear-1 ? '(முந்தைய)' : y===currentYear+1 ? '(அடுத்த)' : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Tamil Month — required for years outside 3-year window */}
        <div style={{marginBottom:'6px'}}>
          <label style={{fontSize:'0.7rem',fontWeight:700,color:T.greenDk}}>
            🏵️ தமிழ் மாதம் {!isInWindow && <span style={{color:'#c62828',fontSize:'0.6rem'}}>(கட்டாயம்)</span>}
          </label>
          <select value={selMonth} onChange={e=>setSelMonth(e.target.value)}
            style={{width:'100%',padding:'8px',borderRadius:'6px',border:`1px solid ${!isInWindow && selMonth==='all' ? '#c62828' : '#ccc'}`,fontWeight:700,fontSize:'0.8rem'}}>
            {isInWindow && <option value="all">அனைத்து மாதங்கள்</option>}
            {T_MONTHS_TA.map((m,i) => <option key={i} value={m}>{m}</option>)}
          </select>
        </div>

        {/* Paksham */}
        <div style={{marginBottom:'6px'}}>
          <label style={{fontSize:'0.7rem',fontWeight:700,color:T.greenDk}}>🌗 பக்ஷம்</label>
          <select value={selPaksham} onChange={e=>setSelPaksham(e.target.value)}
            style={{width:'100%',padding:'8px',borderRadius:'6px',border:'1px solid #ccc',fontWeight:700,fontSize:'0.8rem'}}>
            <option value="all">இரண்டும்</option>
            <option value="sukla">☀️ சுக்ல பக்ஷம் (வளர்பிறை)</option>
            <option value="krishna">🌙 கிருஷ்ண பக்ஷம் (தேய்பிறை)</option>
          </select>
        </div>

        {/* Specific Thithi — 16 base thithis, matches both paksham variants */}
        <div style={{marginBottom:'6px'}}>
          <label style={{fontSize:'0.7rem',fontWeight:700,color:T.greenDk}}>🔢 குறிப்பிட்ட திதி (optional)</label>
          <select value={selThithi} onChange={e=>setSelThithi(e.target.value)}
            style={{width:'100%',padding:'8px',borderRadius:'6px',border:'1px solid #ccc',fontWeight:700,fontSize:'0.8rem'}}>
            <option value="">அனைத்து திதிகளும்</option>
            {BASE_THITHIS.map((t,i) => (
              <option key={i} value={t.name}>
                {t.no}. {t.name} {t.nums.length > 1 ? `(#${t.nums.join(' / #')})` : `(#${t.nums[0]})`}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Warning for non-window years without month */}
      {!isInWindow && selMonth === 'all' && (
        <div style={{background:'#ffebee',padding:'8px',borderRadius:'6px',marginBottom:'8px',fontSize:'0.75rem',color:'#c62828',fontWeight:600,textAlign:'center'}}>
          ⚠️ {selectedYearNum} ஆண்டிற்கு தமிழ் மாதம் தேர்வு செய்யவும்
        </div>
      )}

      {/* Loading */}
      {loading && <div style={{textAlign:'center',padding:'20px',color:T.greenDk,fontWeight:700}}>⏳ Loading...</div>}

      {/* Results count */}
      {!loading && (
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'6px'}}>
          <span style={{fontSize:'0.75rem',color:T.greenDk,fontWeight:700}}>📊 {filtered.length} ச்ராத்த நாட்கள்</span>
          <span style={{fontSize:'0.6rem',color:'#888'}}>
            {isInWindow ? `${selectedYearNum} முழு ஆண்டு` : selMonth !== 'all' ? `${selMonth} — ${selectedYearNum}` : ''}
          </span>
        </div>
      )}

      {/* Results Table */}
      {filtered.length > 0 && (
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.65rem'}}>
            <thead>
              <tr style={{background:T.greenDk,color:'#fff'}}>
                <th style={{padding:'5px',border:'1px solid #ccc',whiteSpace:'nowrap'}}>#</th>
                <th style={{padding:'5px',border:'1px solid #ccc',whiteSpace:'nowrap'}}>English Date</th>
                <th style={{padding:'5px',border:'1px solid #ccc'}}>தமிழ்</th>
                <th style={{padding:'5px',border:'1px solid #ccc'}}>ச்ராத்த திதி</th>
                <th style={{padding:'5px',border:'1px solid #ccc'}}>பக்ஷம்</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d, i) => {
                const isKrishna = d.PakshamNameInTamil?.includes('கிருஷ்ண');
                return (
                  <tr key={i} style={{background: isKrishna ? '#f3e5f5' : i % 2 ? '#f5f5f5' : '#fff'}}>
                    <td style={{padding:'4px',border:'1px solid #eee',textAlign:'center',fontWeight:600}}>{i + 1}</td>
                    <td style={{padding:'4px',border:'1px solid #eee',fontWeight:700,color:T.greenDk,whiteSpace:'nowrap',fontSize:'0.6rem'}}>
                      {formatDate(d.EnglishDate)}
                    </td>
                    <td style={{padding:'4px',border:'1px solid #eee',fontSize:'0.6rem'}}>
                      {d.TamilMonth} {d.TamilDate}
                    </td>
                    <td style={{padding:'4px',border:'1px solid #eee',fontWeight:700,color:T.maroon}}>
                      {d.ShraadhdhaThithiNameInTamil}
                    </td>
                    <td style={{padding:'4px',border:'1px solid #eee',textAlign:'center',fontSize:'0.6rem',
                      color: isKrishna ? '#6a1b9a' : T.greenDk, fontWeight:600}}>
                      {isKrishna ? '🌙' : '☀️'} {d.PakshamNameInTamil}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* No results */}
      {!loading && filtered.length === 0 && selMonth !== 'all' && (
        <div style={{textAlign:'center',padding:'20px',color:'#888',fontSize:'0.8rem'}}>
          இந்த வடிகட்டலில் ச்ராத்த நாட்கள் இல்லை
        </div>
      )}

      {/* Shradhdha Rules */}
      <div style={{marginTop:'12px',background:T.cream,padding:'10px',borderRadius:'8px',border:'1px solid '+T.border}}>
        <div style={{fontWeight:800,fontSize:'0.8rem',color:T.greenDk,marginBottom:'6px'}}>📏 ச்ராத்த விதிகள்</div>
        <div style={{fontSize:'0.65rem',color:T.maroon,lineHeight:1.6}}>
          <p>🔹 <b>அபரான்ன வ்யாபினி</b> திதி ச்ராத்தத்திற்கு உரியது (12:00 PM - 3:30 PM)</p>
          <p>🔹 <b>கிருஷ்ண பக்ஷம்</b> (தேய்பிறை) - ச்ராத்தத்திற்கு மிகவும் உகந்தது</p>
          <p>🔹 திதி நிர்ணயத்தில் <b>1-15 = சுக்ல பக்ஷம்</b>, <b>16-30 = கிருஷ்ண பக்ஷம்</b></p>
          <p>🔹 அமாவாசை நாளில் சர்வ பித்ரு ச்ராத்தம் செய்யலாம்</p>
          <p>🔹 திதி தெரியாவிடில் <b>அமாவாசை</b> அல்லது <b>கிருஷ்ண அஷ்டமி</b> நாளில் செய்யலாம்</p>
        </div>
      </div>

      {/* Tharpana reference images */}
      <div style={{display:'flex',gap:'6px',marginTop:'10px',overflowX:'auto'}}>
        <img src="/images/rituals/sama-pitru-tarpanam.jpg" alt="பித்ரு தர்ப்பணம்" style={{height:'60px',borderRadius:'6px',border:'1px solid '+T.border}} />
        <img src="/images/rituals/sama-deva-tarpanam.jpg" alt="தேவ தர்ப்பணம்" style={{height:'60px',borderRadius:'6px',border:'1px solid '+T.border}} />
        <img src="/images/rituals/sama-rishi-tarpanam.jpg" alt="ரிஷி தர்ப்பணம்" style={{height:'60px',borderRadius:'6px',border:'1px solid '+T.border}} />
      </div>
    </div>
  );
};

export default ShraaddhaThithiPage;